import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { Box, BoxSection } from 'components/_new/Box';
import { usePanel } from 'contexts/panelContext';
import { useAuth } from 'contexts/authContext';
import { classNames } from 'primereact/utils';
import { KnowledgeBaseArticleContentItemMeta } from 'types/KnowledgeBase';
import { IconSvg } from 'components/_new/Icon';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { TaggedArticlesSkeleton } from './skeletons/TaggedArticlesSkeleton';

type TaggedArticleLabels =
	| 'tools-choice-direction'
	| 'tools-choice-solution'
	| 'tools-choice-action'
	| 'tools-choice-thoughts'
	| 'tools-choice-conflicts'
	| 'tools-choice-development';

export const TaggedArticles = () => {
	const { t } = useTranslation();

	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();
	const { knowledgeBaseLinkPrefix, context } = useKnowledgeBase();

	const tags: TaggedArticleLabels[] = [
		'tools-choice-direction',
		'tools-choice-solution',
		'tools-choice-action',
		'tools-choice-thoughts',
		'tools-choice-conflicts',
		'tools-choice-development',
	];
	const { data, isLoading } = useQuery(
		[
			'KNOWLEDGE_BASE_TAG_GROUPPED',
			{
				applicationId: Number(currentApplicationData?.id),
				languageCode: currentUser?.language,
				context,
				tags,
			},
		],
		() => knowledgeBaseApi.getArticlesByLabelsOrIds(tags),
		{
			enabled: true,
			select: (result) => {
				return result as Record<TaggedArticleLabels, KnowledgeBaseArticleContentItemMeta>;
			},
		}
	);

	const getIconByKey = (key: TaggedArticleLabels, className?: string) => {
		switch (key) {
			case 'tools-choice-development':
				return <IconSvg name="square-sprout" className={className} />;
			case 'tools-choice-conflicts':
				return <IconSvg name="cloud-message-zigzag" className={className} />;
			case 'tools-choice-thoughts':
				return <IconSvg name="square-face-sad" className={className} />;
			case 'tools-choice-direction':
				return <IconSvg name="square-compas" className={className} />;
			case 'tools-choice-solution':
				return <IconSvg name="square-signposts" className={className} />;
			case 'tools-choice-action':
			default:
				return <IconSvg name="square-rocket" className={className} />;
		}
	};

	const homepageMode = false;
	const articles = useMemo(() => {
		return Object.entries(data || {}).map(([k, v]) => {
			const icon = getIconByKey(
				k as TaggedArticleLabels,
				classNames('relative z-1', {
					'w-3rem h-3rem': !homepageMode,
					'w-1rem h-1rem': homepageMode,
				})
			);
			return {
				...v,
				icon,
			};
		});
	}, [data, homepageMode]);

	if (isLoading) {
		return <TaggedArticlesSkeleton />;
	}

	return (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>
			{!articles?.length ? (
				<div className="text-muted text-center">{t('misc.empty')}</div>
			) : (
				<div className="grid knowledge-base-purpose-grid">
					{articles.map((item) => {
						const { id, title, icon } = item;
						const IconComponent = () => icon;
						const pathname = `${knowledgeBaseLinkPrefix}/article/${item.id}`;
						return (
							<Link
								key={id}
								to={{ pathname }}
								className={classNames('knowledge-base-card-link flex flex-column col-12', {
									'sm:col-6 md:col-4': !homepageMode,
									'md:col-6': homepageMode,
								})}
								onClick={() => {
									setTimeout(() => {
										document.body.scrollTop = 0;
										document.getElementById('scrollWrapperRef')?.scrollTo({ top: 0 });
										document.getElementById('pageScrollWrapperRef')?.scrollTo({ top: 0 });
									}, 100);
								}}
							>
								<Box
									wrap
									hoverable
									slim={homepageMode}
									variant="white-bordered"
									className={classNames('knowedge-base-box knowledge-base-box h-full', {
										'box-directory-link': homepageMode,
									})}
								>
									<BoxSection
										horizontal
										contentClassName={classNames('h-full justify-content-start', {
											'align-items-start': ['program-admin', 'coordinator'].includes(context),
											'align-items-center': context === 'user-panel',
										})}
									>
										<div className="flex justify-content-start">
											<div className="knowedge-base-box-icon text-darkrose">
												<div className="circle-inbg" />
												<IconComponent />
											</div>
										</div>

										<div className="flex-1 px-2">
											<div className="font-semibold">{title}</div>
										</div>
									</BoxSection>
								</Box>
							</Link>
						);
					})}
				</div>
			)}
		</>
	);
};
